import React, { useState, useEffect } from 'react';
import { Package, ChevronDown, ChevronUp, X } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { formatApiError } from '../../utils/apiError';
import { createTextPicker, getLocaleFromLang } from '../../utils/localizedContent';


const STATUS_OPTIONS = ['pending', 'paid', 'processing', 'shipped', 'delivered', 'refunded', 'partially_refunded', 'cancelled'];

const STATUS_COLOR = {
  pending:    'bg-[#F59E0B]/20 text-[#F59E0B]',
  paid:       'bg-[#10B981]/20 text-[#34D399]',
  processing: 'bg-[#3B82F6]/20 text-[#3B82F6]',
  shipped:    'bg-[#8B5CF6]/20 text-[#A78BFA]',
  delivered:  'bg-[#10B981]/20 text-[#10B981]',
  refunded:   'bg-[#F59E0B]/20 text-[#F59E0B]',
  partially_refunded: 'bg-[#F97316]/20 text-[#FB923C]',
  cancelled:  'bg-[#EF4444]/20 text-[#EF4444]',
};

export default function AdminOrders({ orders, setOrders, lang, setMessage, highlightOrderId, onHighlightConsumed }) {
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundTargetOrder, setRefundTargetOrder] = useState(null);
  const [refundMode, setRefundMode] = useState('full');
  const [refundReason, setRefundReason] = useState('requested_by_customer');
  const [refundAmount, setRefundAmount] = useState('');
  const l = createTextPicker(lang);

  useEffect(() => {
    if (!highlightOrderId) return;
    const exists = orders.some((o) => o.order_id === highlightOrderId);
    if (!exists) return;
    setExpandedOrder(highlightOrderId);
    const element = document.querySelector(`[data-order-id="${highlightOrderId}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    if (typeof onHighlightConsumed === 'function') {
      onHighlightConsumed();
    }
  }, [highlightOrderId, orders, onHighlightConsumed]);

  const handleStatusChange = async (orderId, status) => {
    try {
      const params = { status };
      if (status === 'refunded' || status === 'partially_refunded') {
        const target = orders.find((o) => o.order_id === orderId);
        setRefundTargetOrder(target || null);
        setRefundMode(status === 'partially_refunded' ? 'partial' : 'full');
        setRefundReason('requested_by_customer');
        setRefundAmount(String(target?.total || ''));
        setRefundModalOpen(true);
        return;
      }
      const { data } = await axios.patch(`${API}/admin/orders/${orderId}/status`, null, { params });
      setOrders(prev => prev.map(o => o.order_id === orderId ? {
        ...o,
        status: data?.status || status,
        refunded_amount: data?.refunded_amount ?? o.refunded_amount,
      } : o));
      setMessage(l('Estado actualizado', 'Status updated'));
    } catch (err) { setMessage(formatApiError(err)); }
  };

  const submitRefund = async () => {
    if (!refundTargetOrder) return;
    const isPartial = refundMode === 'partial';
    const params = {
      status: isPartial ? 'partially_refunded' : 'refunded',
      refund_reason: refundReason || 'requested_by_customer',
    };
    if (isPartial) {
      const amount = Number(refundAmount);
      if (!Number.isFinite(amount) || amount <= 0 || amount > Number(refundTargetOrder.total || 0)) {
        setMessage(l('Importe inválido para reembolso parcial', 'Invalid partial refund amount'));
        return;
      }
      params.refund_amount = amount;
    }
    try {
      const { data } = await axios.patch(`${API}/admin/orders/${refundTargetOrder.order_id}/status`, null, { params });
      setOrders(prev => prev.map(o => o.order_id === refundTargetOrder.order_id ? {
        ...o,
        status: data?.status || params.status,
        refunded_amount: data?.refunded_amount ?? o.refunded_amount,
      } : o));
      setMessage(l('Reembolso aplicado correctamente', 'Refund applied successfully'));
      setRefundModalOpen(false);
      setRefundTargetOrder(null);
    } catch (err) {
      setMessage(formatApiError(err));
    }
  };

  return (
    <div>
      <h2 className="text-white font-bold text-xl mb-4">{l('Pedidos', 'Orders')} ({orders.length})</h2>
      <div className="space-y-3">
        {orders.length === 0 && (
          <p className="text-[#A1A1AA] text-center py-8 text-sm">{l('No hay pedidos registrados', 'No orders yet')}</p>
        )}
        {orders.map(o => {
          const displayOrderId = o.order_alias || o.order_id;
          return (
          <div
            key={o.order_id}
            data-order-id={o.order_id}
            className={`bg-[#12121A] border rounded-xl overflow-hidden ${highlightOrderId === o.order_id ? 'border-[#C9A962]/60 shadow-[0_0_0_1px_rgba(201,169,98,0.25)]' : 'border-white/5'}`}
          >
            <div className="flex items-center gap-4 p-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-white font-semibold text-sm truncate">{o.user_name || o.user_email || 'Cliente'}</p>
                  <span className="text-[#52525B] text-xs">{displayOrderId}</span>
                </div>
                <p className="text-[#A1A1AA] text-sm">
                  {new Date(o.created_at || o.paid_at || '').toLocaleDateString(getLocaleFromLang(lang), { day: '2-digit', month: 'short', year: 'numeric' })}
                  {' · '}<span className="text-[#C9A962] font-semibold">{o.total?.toFixed(2)}€</span>
                  {' · '}{(o.items || []).length} {l('artículo(s)', 'item(s)')}
                  {o.refunded_amount ? ` · ${l('Reembolsado', 'Refunded')}: ${Number(o.refunded_amount).toFixed(2)}€` : ''}
                </p>
              </div>
              <select
                value={o.status || 'pending'}
                onChange={e => handleStatusChange(o.order_id, e.target.value)}
                className={`text-xs font-semibold px-2 py-1 rounded-full border-0 outline-none cursor-pointer ${STATUS_COLOR[o.status] || 'bg-white/10 text-white'}`}
                style={{ backgroundColor: 'transparent' }}
              >
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <button
                onClick={() => setExpandedOrder(expandedOrder === o.order_id ? null : o.order_id)}
                className="p-1.5 rounded-lg text-[#A1A1AA] hover:text-white"
              >
                {expandedOrder === o.order_id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>
            {expandedOrder === o.order_id && (
              <div className="border-t border-white/5 px-4 py-3 bg-[#0A0A0E]/40 space-y-2">
                {(o.items || []).map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <Package size={14} className="text-[#A1A1AA] flex-shrink-0" />
                    <span className="text-white text-sm flex-1">{item.name || item.product_name}</span>
                    <span className="text-[#A1A1AA] text-xs">{item.quantity}x</span>
                    <span className="text-[#C9A962] text-sm font-medium">{(item.price * item.quantity).toFixed(2)}€</span>
                  </div>
                ))}
                {o.notes && <p className="text-[#52525B] text-xs italic">{o.notes}</p>}
              </div>
            )}
          </div>
          );
        })}
      </div>
      {refundModalOpen && refundTargetOrder && (
        <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm p-4 flex items-center justify-center">
          <div className="w-full max-w-md rounded-2xl bg-[#12121A] border border-[#C9A962]/25 shadow-2xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-[#F5F0E6] font-bold text-lg">
                {l('Gestionar reembolso', 'Manage refund')}
              </h3>
              <button
                type="button"
                onClick={() => setRefundModalOpen(false)}
                className="text-[#A1A1AA] hover:text-white"
                aria-label={l('Cerrar', 'Close')}
              >
                <X size={16} />
              </button>
            </div>
            <div className="text-xs text-[#A1A1AA]">
              {l('Pedido', 'Order')}: <span className="text-[#F5F0E6]">{refundTargetOrder.order_id}</span> ·
              {' '}{l('Total', 'Total')}: <span className="text-[#C9A962]">{Number(refundTargetOrder.total || 0).toFixed(2)}€</span>
            </div>
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wide text-[#52525B]">{l('Tipo de reembolso', 'Refund type')}</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setRefundMode('full')}
                  className={`px-3 py-2 rounded-lg text-sm border ${refundMode === 'full' ? 'border-[#C9A962] text-[#C9A962] bg-[#C9A962]/10' : 'border-white/10 text-[#A1A1AA]'}`}
                >
                  {l('Total', 'Full')}
                </button>
                <button
                  type="button"
                  onClick={() => setRefundMode('partial')}
                  className={`px-3 py-2 rounded-lg text-sm border ${refundMode === 'partial' ? 'border-[#C9A962] text-[#C9A962] bg-[#C9A962]/10' : 'border-white/10 text-[#A1A1AA]'}`}
                >
                  {l('Parcial', 'Partial')}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-[#52525B] mb-1">
                {l('Motivo', 'Reason')}
              </label>
              <select
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                className="w-full bg-[#0A0A0E] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
              >
                <option value="requested_by_customer">{l('Solicitado por cliente', 'Requested by customer')}</option>
                <option value="duplicate">{l('Cobro duplicado', 'Duplicate')}</option>
                <option value="fraudulent">{l('Fraudulento', 'Fraudulent')}</option>
              </select>
            </div>
            {refundMode === 'partial' && (
              <div>
                <label className="block text-xs uppercase tracking-wide text-[#52525B] mb-1">
                  {l('Importe a reembolsar (€)', 'Refund amount (€)')}
                </label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  max={Number(refundTargetOrder.total || 0)}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  className="w-full bg-[#0A0A0E] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setRefundModalOpen(false)}
                className="px-3 py-2 rounded-lg text-sm text-[#A1A1AA] border border-white/10"
              >
                {l('Cancelar', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={submitRefund}
                className="btn-primary px-4 py-2 text-sm"
              >
                {l('Confirmar reembolso', 'Confirm refund')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
